import React from 'react';
import { canonicalizePublicMediaUrl, isNetlifyOptimizableMediaUrl, NETLIFY_IMAGE_WIDTHS } from '../../lib/mediaUrl';

function shouldUseNetlifyImageCdn() {
  if (typeof window === 'undefined') {
    return false;
  }

  return !/localhost|127\.0\.0\.1/.test(window.location.hostname);
}

export function canOptimizeWithNetlify(src) {
  return shouldUseNetlifyImageCdn() && isNetlifyOptimizableMediaUrl(src);
}

export function buildNetlifyImageUrl(src, width, fit = 'cover') {
  const canonicalSrc = canonicalizePublicMediaUrl(src);

  return `/.netlify/images?url=${encodeURIComponent(canonicalSrc)}&w=${width}&fit=${fit}&q=82`;
}

export function buildNetlifyImageSrcSet(src, fit = 'cover') {
  return NETLIFY_IMAGE_WIDTHS.map((size) => `${buildNetlifyImageUrl(src, size, fit)} ${size}w`).join(', ');
}

export function OptimizedImage({
  src,
  alt,
  className = '',
  sizes = '(min-width: 1100px) 33vw, 100vw',
  fit = 'cover',
  width,
  height,
  loading = 'lazy',
  decoding = 'async',
  fetchPriority,
  onError,
  ...imgProps
}) {
  const [failedOptimizedSrc, setFailedOptimizedSrc] = React.useState(null);

  if (!src) {
    return null;
  }

  const canonicalSrc = canonicalizePublicMediaUrl(src);
  const optimized = canOptimizeWithNetlify(canonicalSrc) && failedOptimizedSrc !== canonicalSrc;
  const imageSrc = optimized ? buildNetlifyImageUrl(canonicalSrc, 1200, fit) : canonicalSrc;
  const srcSet = optimized ? buildNetlifyImageSrcSet(canonicalSrc, fit) : undefined;
  const priorityProps = fetchPriority ? { fetchpriority: fetchPriority } : {};

  function handleError(event) {
    if (optimized) {
      setFailedOptimizedSrc(canonicalSrc);
      return;
    }

    onError?.(event);
  }

  return (
    <img
      src={imageSrc}
      srcSet={srcSet}
      sizes={srcSet ? sizes : undefined}
      alt={alt}
      className={className}
      loading={loading}
      decoding={decoding}
      width={width}
      height={height}
      onError={handleError}
      {...imgProps}
      {...priorityProps}
    />
  );
}
